import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IChat, IChatbotState } from "../types/chatbot";
import { nanoid } from "nanoid";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getClient } from "../utils/http";
// import { decrypt, encrypt } from "../utils/encryption";
import {
  jsonToTableMarkdown,
  sanitizeUserInputQuery,
  toTitleCaseFromSnake,
} from "../utils/common";
import { useAuth } from "./auth_provider";
import { selfHelpApplications } from "../utils/constant";

const datasetName = process.env.REACT_APP_DATASET_NAME;
const perspective = process.env.REACT_APP_PERSPECTIVE;

const ChatbotContext = createContext<IChatbotState | null>(null);

const getNewChat = () => {
  return { sessionId: nanoid(), title: "New chat", conversations: [] };
};

export function ChatbotProvider({
  children,
  tool,
}: PropsWithChildren<{ tool: string }>) {
  const [selectedChat, setSelectedChat] = useState(0);
  const [selectedQuery, setSelectedQuery] = useState<number | null>(null);
  const [last5Queries, setLast5Queries] = useState<any>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>("");
  const [fileList, setFileList] = useState([]);
  const [applications, setApplications] = useState(selfHelpApplications);
  const [chats, setChats] = useState<IChat[]>([getNewChat()]);
  const { pathname } = useLocation();
  const { source } = useAuth();
  const navigate = useNavigate();
  const conversationsContainerRef = useRef<HTMLDivElement>();
  const [searchParams] = useSearchParams();
  const docChatType = searchParams.get("type") || "document";
  const [activePolicies, setActivePolicies] = useState<Record<
    string,
    any
  > | null>(null);
  const [wcClaimDetails, setWCClaimDetails] = useState<Record<
    string,
    any
  > | null>(null);
  const [isDocsProcessing, setIsDocsProcessing] = useState(false);

  const isChatLoading = useMemo(() => {
    let conversations = chats?.[selectedChat]?.conversations;
    return !!conversations?.[conversations?.length - 1]?.isLoading;
  }, [chats, selectedChat]);

  const scrollDown = () => {
    if (conversationsContainerRef.current) {
      conversationsContainerRef.current.scrollTop =
        conversationsContainerRef.current.scrollHeight;
    }
  };

  const newChat = useCallback(() => {
    setChats([...chats, getNewChat()]);
    setSelectedChat(chats.length);
  }, [chats]);

  const deleteChat = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    const filteredChats = chats.filter((chat) => chat.sessionId !== id);
    if (filteredChats.length === 0) {
      filteredChats.push(getNewChat());
    }
    if (selectedChat === chats.length - 1) {
      setSelectedChat(filteredChats.length - 1);
    }
    setChats([...filteredChats]);
  };

  const getLORMarkdown = (details: any, isTitle: boolean = false) => {
    const title = `\n\n### LOR (List of Requirements) Checklist\n\n`;
    let jsonData = details?.map((data: any) => ({
      Filename: data?.filename,
      "Document Type": data?.document_type || "N/A",
      "File Type": data?.file_type || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };

  const getLORStatusMarkdown = (details: any, isTitle: boolean = false) => {
    const title = `\n\n### LOR (List of Requirements) Checklist Status\n\n`;
    let jsonData = Object.keys(details)?.map((key: any) => ({
      Detail: toTitleCaseFromSnake(key || ""),
      Information: `${details?.[key]}` || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };

  const getAssessmentMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Assessment Table\n\n";
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)?.map((key: any) => ({
      Detail: toTitleCaseFromSnake(key || ""),
      Information: details?.[key] || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };

  const getTableMarkdown = (details: any, isTitle: boolean = false, titleValue :any) => {
    const title = titleValue;
    if (!details || typeof details !== "object") {
    return `${isTitle ? title : ""}Detail Not Found`;
    }
    const keys = Object.keys(details);
    if (!keys.length) {
    return `${isTitle ? title : ""}Detail Not Found`;
    }
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)?.map((key: any) => ({
      Detail: toTitleCaseFromSnake(key || ""),
      Information: details?.[key] || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getRiskReportMarkdown = (details: any, isTitle: boolean = false, titleValue :any) => {
    const title = titleValue;
    if (!details || !details?.length) return "";
    let jsonData = details?.map((data: any) => ({
      "Parameter": data?.parameter || "N/A",
      "Details": data?.details || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getMitigationMarkdown = (details: any, isTitle: boolean = false, titleValue :any) => {
    const title = titleValue;
    if (!details || !details?.length) return "";
    let jsonData = details?.map((data: any) => ({
      "Action": data?.action || "N/A",
      "Impact": data?.impact || "N/A",
      "Cost": data?.cost || "N/A",
      "TimeFrame": data.timeframe || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getHazardControlMarkdown = (
  details: any,
  isTitle: boolean = false,
  titleValue: any
) => {
  if (!details || typeof details !== "object") return "";

  let finalOutput = "";

  const formatList = (arr: any) => {
    if (!arr) return "N/A";
    if (!Array.isArray(arr)) return arr;
    if (arr.length === 0) return "N/A";

    return arr.map((item: string) => `• ${item}`).join("<br>");
  };

  Object.entries(details).forEach(([category, items]: any) => {
    // ✅ FIX: Ensure items is always an array
    const safeItems = Array.isArray(items)
      ? items
      : items
      ? [items]
      : [];

    if (!safeItems.length) return;

    const title = `${titleValue || ""}${category.replace(/_/g, " ")}`;

    let jsonData = safeItems.map((data: any) => ({
      "Hazard Identified": data?.hazard_identified || "N/A",
      "Mitigation Existing Controls": formatList(
        data?.mitigation_existing_controls
      ),
      "Mitigation Recommendations": formatList(
        data?.mitigation_recommendations
      ),
      "Mitigation Recommendations By LLM": formatList(
        data?.mitigation_recommendations_by_llm
      ),
    }));

    finalOutput += `
${isTitle ? `### ${title}\n` : ""}
${jsonToTableMarkdown(jsonData)}

`;
  });

  return finalOutput;
};
  const getClaimNoteMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Claim Note\n\n";
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)
      ?.filter((key: any) => key !== "assessment_table")
      ?.map((key: any) => ({
        Detail: toTitleCaseFromSnake(key || ""),
        Information: details?.[key] || "N/A",
      }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getClaimTypeMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Claim Type\n\n";
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)?.map((key: any) => ({
      Detail: toTitleCaseFromSnake(key || ""),
      Information: details?.[key] || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getEventSummaryMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Event Summary\n\n";
    if (!details) return title + "Detail Not Found";
    return `${isTitle ? title : ""}${details}`;
  };
  const getMedicalExpenseMarkdown = (
    details: any,
    isTitle: boolean = false,
  ) => {
    const title = "\n\n### Medical Expense\n\n";
    if (!details || !details?.length) return "";
    let jsonData = details?.map((data: any) => ({
      Date: data?.date,
      Invoice: data?.invoice,
      Amount: data?.amount,
    }));
    return `\n\n${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getProcessSummaryMarkdown = (
    details: any,
    isTitle: boolean = false,
  ) => {
    const title = "\n\n### Process Summary\n\n";
    if (!details) return title + "Detail Not Found";
    return `${isTitle ? title : ""}${details}`;
  };
  const getGoodBadFeaturesMarkdown = (
  details: any,
  isTitle: boolean = false,
  titleValue: any
) => {
  const title = titleValue;
  if (!details || !Array.isArray(details)) {
    return `${isTitle ? title : ""}Detail Not Found`;
  }
  const formattedText = details
    .map((item: string) => {
      if (!item) return "";
      const parts = item.split(":");
      // First part → Bold title
      const heading = parts[0];
      const rest = parts.slice(1).join(":");
      return `• **${heading}:** ${rest.trim()}`;
    })
    .join("\n");

  return `${isTitle ? title : ""}\n${formattedText}`;
};
  const getTextMarkdown = (
    details: any,
    isTitle: boolean = false,
    titleValue : any, 
  ) => {
    const title = titleValue;
    if (!details) return title + "Detail Not Found";
    return `${isTitle ? title : ""}${details}`;
  };
  const getPolicyDetailsMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Policy Details\n\n";
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)?.map((key: any) => ({
      Detail: toTitleCaseFromSnake(key || ""),
      Information: details?.[key] || "N/A",
    }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getLossDetailsMarkdown = (details: any, isTitle: boolean = false) => {
    const title = `\n\n### Loss Details\n\n`;
    if (!Object.keys(details)?.length) return title + "Detail Not Found";
    let jsonData = Object.keys(details)
      ?.filter((key: any) => key !== "full_summary")
      ?.map((key: any) => ({
        Detail: toTitleCaseFromSnake(key || ""),
        Information: details?.[key] || "N/A",
      }));
    return `${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getInvoiceMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Invoice Details\n\n";
    if (!details || !details?.length) return "";
    let jsonData = details?.map((data: any) => ({
      "Name of Issuer": data?.name_of_issuer,
      "Name of Receiver": data?.name_of_receiver,
      "Invoice Date": data?.invoice_date,
      "Reference Number": data?.reference_number,
      Amount: data?.invoice_amount,
    }));
    return `\n\n${isTitle ? title : ""}${jsonToTableMarkdown(jsonData)}`;
  };
  const getDatesMarkdown = (details: any, isTitle: boolean = false) => {
    const title = "\n\n### Retroactive Dates\n\n";
    if (!Object.keys(details)?.length) return "";
    let markdownString = "";
    Object.keys(details?.retroactive_dates)?.forEach((key) => {
      let clauseDetails = details?.retroactive_dates?.[key];
      let jsonData = clauseDetails?.map((data: any) => ({
        Date: data?.date || "N/A",
        "Limit of Liability": data?.limit_of_liability || "N/A",
      }));
      markdownString += `\n${key}:\n${jsonToTableMarkdown(jsonData)}\n`;
    });
    return `\n\n${isTitle ? title : ""}${markdownString}\n\n### Pending or Prior Date\n ${details?.["pending_or_prior_date"]}`;
  };
  const getMarkdownDetails = (key: string) => {
    if (key === "lor_checklist") {
      let details = wcClaimDetails?.[key] || {};
      if (!details?.length) return "Detail Not Found";
      return `${getLORMarkdown(details || {}, true)}${getLORStatusMarkdown(
        wcClaimDetails?.["lor_checklist_status"] || {},
        true,
      )}`;
    } else if (key === "claim_summary") {
      let medicalExpense = wcClaimDetails?.["medical_expense"];
      if (medicalExpense && medicalExpense?.length)
        medicalExpense?.push({
          date: "Total",
          invoice: "",
          amount: wcClaimDetails?.["medical_expense_total"],
        });
      return `${getClaimNoteMarkdown(
        wcClaimDetails?.["claim_note"] || {},
        true,
      )}${getAssessmentMarkdown(
        wcClaimDetails?.["claim_note"]?.["assessment_table"] || {},
        true,
      )}${getEventSummaryMarkdown(
        wcClaimDetails?.["event_summary"] || null,
        true,
      )}${getMedicalExpenseMarkdown(medicalExpense)}${getClaimTypeMarkdown(
        wcClaimDetails?.["claim_type"] || {},
        true,
      )}${getProcessSummaryMarkdown(
        wcClaimDetails?.["process_summary"] || null,
        true,
      )}`;
    } else if (key === "discharge_summary") {
      if (wcClaimDetails && Object.keys(wcClaimDetails).length > 0) {
        let dvData = wcClaimDetails?.["claim_note"];
        let dvData1 = wcClaimDetails?.["claim_note"]?.["assessment_table"];
        let Compensation = (dvData1?.Total_compensation) - (dvData1?.Medical_expenses);
        return ` 
      Claim No	: ${dvData?.Claim_No}\n
      Policy No	: ${dvData?.Policy_No}\n
      Policy period : ${dvData?.POL_START_DATE} - ${dvData?.POL_END_DATE}\n\n

      ${dvData?.Insured} notified about an accident dated ${dvData?.Date_of_Accident} resulting in an injury to \n
      ${dvData?.Name_of_the_Worker} (aged ${dvData?.Age}) and employed in ${dvData?.Insured} arising out of the said accident.\n\n

      The insurer shall pay INR ${dvData1.Total_compensation}/- and ${dvData?.Insured} has freely, willingly & without any\n
      compulsion accepted an amount of INR ${dvData1?.Total_compensation}/- in favour of ${dvData?.Insured} towards full\n
      and final settlement, discharge, extinguishment & satisfaction of any and all claims and demands \n
      that have been made; or which could be made or may at anytime herein after be made under or in \n
      relation to the policy/act against the said accident.\n\n

      ${dvData?.Insured} agrees & affirms that they have paid to ${dvData?.Name_of_the_Worker} an amount of INR \n
      ${Compensation}/- as compensation and an amount of INR ${dvData1?.Medical_expenses}/- to Hospital\n
      towards medical expenses of ${dvData?.Name_of_the_Worker} (which has also been declared by the below\n
      claimant written declaration and not claimed any other policies).\n\n

      ${dvData?.Insured} hereby discharge the insurer against any future liability under\n
      this claim upon receipt of the NEFT payment of INR ${dvData1?.Total_compensation}/- \n\n

      Medical expenses are covered under this policy upto a limit of INR ${dvData?.per_limit}/- per \n
      accident and an aggregate limit of upto INR ${dvData?.aggregate_limit}/-.\n
      I/we hereby voluntarily give discharge receipt to the insurance company towards full and final\n
      settlement of the claim arising in respect of said loss/accident.\n\n

      Authorized Signatory with Name & Stamp: ${dvData?.Insured}\n 
      Date :\n
      Claimant Signature :  ${dvData?.Name_of_the_Worker}`
      }
    } else if (key === "tech_eo_summary") {
      let invoiceDetails = wcClaimDetails?.["invoices_table"]?.rows || [];
      let policyDates = wcClaimDetails?.["policy_dates"] || {};
      if (invoiceDetails && invoiceDetails?.length)
        invoiceDetails?.push({
          name_of_issuer: "Total",
          name_of_receiver: "",
          invoice_date: "",
          reference_number: "",
          invoice_amount: wcClaimDetails?.["invoices_table"]?.total_amount,
        });
      return `${getPolicyDetailsMarkdown(
        wcClaimDetails?.["policy_details"] || {},
        true,
      )}${getLossDetailsMarkdown(
        wcClaimDetails?.["loss_details"] || {},
        true,
      )}${getInvoiceMarkdown(invoiceDetails, true)}${getDatesMarkdown(policyDates, true)}`;
    } else if (key === "tech_do_summary") {
      let invoiceDetails = wcClaimDetails?.["invoices_table"]?.rows || [];
      let policyDates = wcClaimDetails?.["policy_dates"] || {};
      if (invoiceDetails && invoiceDetails?.length)
        invoiceDetails?.push({
          name_of_issuer: "Total",
          name_of_receiver: "",
          invoice_date: "",
          reference_number: "",
          invoice_amount: wcClaimDetails?.["invoices_table"]?.total_amount,
        });
      return `${getPolicyDetailsMarkdown(
        wcClaimDetails?.["policy_details"] || {},
        true,
      )}${getLossDetailsMarkdown(wcClaimDetails?.["loss_details"] || {}, true)}
      ${getInvoiceMarkdown(invoiceDetails, true)}${getDatesMarkdown(policyDates, true)}`;
    } else if (key === "executive_summary") {
      return `${getTableMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["RISK_DETAILS"] || {},
        true, "\n\n### RISK_DETAILS\n\n",
      )}${getRiskReportMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["risk_report_score"] || {},
        true, "\n\n### RISK REPORT DETAILS\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["EXECUTIVE_SUMMARY"] || null,
        true, "\n\n### EXECUTIVE SUMMARY\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_RECOMMENDATIONS_WITH_MITIGATION_MEASURES"] || null,
        true,"\n\n### TOP 5 RECOMMENDATIONS WITH MITIGATION MEASURES\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_BAD_FEATURES_BASED_ON_IMPACT_ON_OVERALL_RISK"] || null,
        true, "\n\n### TOP 5 BAD FEATURES BASED ON IMPACT ON OVERALL RISK\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_GOOD_FEATURES_BASED_ON_IMPACT_ON_OVERALL_RISK"] || null,
        true, "\n\n### TOP 5 GOOD FEATURES BASED ON IMPACT ON OVERALL RISK\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["OVERALL_RISK_IMPRESSION_AS_PER_VISITING_RISK_ENGINEER"] || null,
        true, "\n\n### OVERALL RISK IMPRESSION AS PER VISITING RISK ENGINEER\n\n"
      )}`;
    } else if (key === "detailed_summary") {
      return `${getTableMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["RISK_DETAILS"] || {},
        true, "\n\n### RISK_DETAILS\n\n",
      )}${getRiskReportMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["risk_report_score"] || {},
        true, "\n\n### RISK REPORT SCORE\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["detailed_summary"] || {},
        true, "\n\n### DETAILED SUMMARY\n\n",
      )}${getHazardControlMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["hazard_control_mapper"] || {},
        true, "\n\n### HAZARD CONTROL MAPPER\n\n",
      )}${getMitigationMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["mitigation_recommender"] || {},
        true, "\n\n### MITIGATION RECOMMENDER\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["bad_features"] || {},
        true, "\n\n### BAD FEATURES\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["good_features"] || null,
        true, "\n\n### GOOD FEATURES\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["loss_history_explainer"] || null,
        true,"\n\n### LOSS HISTORY EXPLAINER\n\n",
      )}${getTableMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["natcat_exposure_snapshot"] || null,
        true, "\n\n### NATCAT EXPOSURE SNAPSHOT\n\n",
      )}`;
    } else if (key === "combined_summary") {
       return `${getTableMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["RISK_DETAILS"] || {},
        true, "\n\n### RISK_DETAILS\n\n",
      )}${getRiskReportMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["risk_report_score"] || {},
        true, "\n\n### RISK REPORT DETAILS\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["EXECUTIVE_SUMMARY"] || null,
        true, "\n\n### EXECUTIVE SUMMARY\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_RECOMMENDATIONS_WITH_MITIGATION_MEASURES"] || null,
        true,"\n\n### TOP 5 RECOMMENDATIONS WITH MITIGATION MEASURES\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_BAD_FEATURES_BASED_ON_IMPACT_ON_OVERALL_RISK"] || null,
        true, "\n\n### TOP 5 BAD FEATURES BASED ON IMPACT ON OVERALL RISK\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["TOP_5_GOOD_FEATURES_BASED_ON_IMPACT_ON_OVERALL_RISK"] || null,
        true, "\n\n### TOP 5 GOOD FEATURES BASED ON IMPACT ON OVERALL RISK\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["EXECUTIVE_SUMMARY_SECTION"]?.["OVERALL_RISK_IMPRESSION_AS_PER_VISITING_RISK_ENGINEER"] || null,
        true, "\n\n### OVERALL RISK IMPRESSION AS PER VISITING RISK ENGINEER\n\n"
      )}${getTableMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["RISK_DETAILS"] || {},
        true, "\n\n### RISK_DETAILS\n\n",
      )}${getRiskReportMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["risk_report_score"] || {},
        true, "\n\n### RISK REPORT SCORE\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["detailed_summary"] || {},
        true, "\n\n### DETAILED SUMMARY\n\n",
      )}${getHazardControlMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["hazard_control_mapper"] || {},
        true, "\n\n### HAZARD CONTROL MAPPER\n\n",
      )}${getMitigationMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["mitigation_recommender"] || {},
        true, "\n\n### MITIGATION RECOMMENDER\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["bad_features"] || {},
        true, "\n\n### BAD FEATURES\n\n",
      )}${getGoodBadFeaturesMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["good_features"] || null,
        true, "\n\n### GOOD FEATURES\n\n",
      )}${getTextMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["loss_history_explainer"] || null,
        true,"\n\n### LOSS HISTORY EXPLAINER\n\n",
      )}${getTableMarkdown(
        wcClaimDetails?.["DETAILED_SUMMARY_SECTION"]?.["natcat_exposure_snapshot"] || null,
        true, "\n\n### NATCAT EXPOSURE SNAPSHOT\n\n",
      )}`;
    } else {
      return "";
    }
  };

  const addConversation = (
    query: string,
    locale: string,
    vectorName: string,
    inputType?: any,
  ) => {
    if (["wc-claims", "tech-eo", "tech-do", "dtr"].includes(tool)) {
      const markdownResult = getMarkdownDetails(inputType);
      setChats((chats) => {
        chats?.[selectedChat]?.conversations?.push({
          query: query,
          locale: locale,
          isLoading: false,
          inputType: inputType,
          result: markdownResult || "",
        });

        const updatedChats = [...chats];
        return updatedChats;
      });
      return;
    }
    setChats((chats) => {
      chats?.[selectedChat]?.conversations?.push({
        query: query,
        locale: locale,
        isLoading: true,
        inputType: inputType || "query",
      });

      const updatedChats = [...chats];
      fetchAnswer(
        updatedChats?.[selectedChat]?.conversations?.length - 1,
        vectorName,
      );
      return updatedChats;
    });
  };
  const addConversationFromHistory = (data: any, tool?: string) => {
    if (tool === "Chat @ KYP") {
      const chatHistory: any = {
        sessionId: data.conversationId,
        title: "New chat",
        conversations: [],
      };
      console.log(data);
      data?.messages?.forEach((data: any, index: Number) => {
        const indexToBeUpdated = chatHistory.conversations.findIndex(
          (item: any) => item.id === data.id,
        );
        if (indexToBeUpdated === -1) {
          chatHistory.conversations.push({
            id: data?.id,
            query: data?.sender === "user" ? data?.message : "",
            locale: data?.locale,
            isLoading: false,
            sqlQuery: data?.sql_query,
            result: data?.sender === "bot" ? data?.message : "",
            isPlottable: data?.is_plottable,
            message: "kyp",
            displyType: data?.displyType,
            questionSuggestion: data?.questionSuggestion,
            raw: data,
            url: data?.insuranceUrl,
            comparison: data?.comparison,
            isLiked: data.feedback,
          });
        } else {
          if (indexToBeUpdated >= 0) {
            chatHistory.conversations[indexToBeUpdated] = {
              ...chatHistory.conversations[indexToBeUpdated],
              isLiked: data.feedback,
              result:
                data?.sender === "bot"
                  ? data?.message
                  : chatHistory.conversations[indexToBeUpdated].result
                    ? chatHistory.conversations[indexToBeUpdated].result
                    : "",
              query:
                data?.sender === "user"
                  ? data?.message
                  : chatHistory.conversations[indexToBeUpdated].query
                    ? chatHistory.conversations[indexToBeUpdated].query
                    : "",
            };
          }
        }
      });
      setChats([chatHistory]);
      return;
    }

    const request = JSON.parse(data.request);
    const response = JSON.parse(data.response);
    const conversations = chats?.[selectedChat]?.conversations;

    if (conversations?.[conversations?.length - 1]?.query === request?.query) {
      return;
    }
    setChats([
      ...chats,
      {
        sessionId: nanoid(),
        title: "New chat",
        conversations: [
          {
            query: request?.query,
            locale: response?.locale,
            isLoading: false,
            sqlQuery: response?.sql_query,
            result: response?.result,
            isPlottable: response?.is_plottable,
            message: response?.message,
            displyType: response?.displyType,
            questionSuggestion: response?.questionSuggestion,
            raw: response,
            url: response?.insuranceUrl,
            comparison: response?.comparison,
          },
        ],
      },
    ]);
    setSelectedChat(chats.length);
  };

  const passSuggestion = (suggestion: string) => {
    setSelectedSuggestion(suggestion);
  };

  const like = (chatIndex: number) => {
    if (selectedQuery === null) {
      feedbackSubmit(chatIndex, true);
      return;
    }

    if (selectedQuery !== null) {
      setLast5Queries((queries: any) => {
        queries[selectedQuery].conversations[0].isLiked = true;
        return [...queries];
      });
    } else {
      setChats((chats) => {
        chats[selectedChat].conversations[chatIndex].isLiked = true;
        return [...chats];
      });
    }
  };

  const dislike = (chatIndex: number, feedbackReason?: any) => {
    if (selectedQuery === null) {
      feedbackSubmit(chatIndex, false, feedbackReason);
      return;
    }
    if (selectedQuery !== null) {
      setLast5Queries((queries: any) => {
        queries[selectedQuery].conversations[0].isLiked = false;
        return [...queries];
      });
    } else {
      setChats((chats) => {
        chats[selectedChat].conversations[chatIndex].isLiked = false;
        return [...chats];
      });
    }
  };

  const feedbackSubmit = async (
    chatIndex: number,
    isLiked: boolean,
    feedbackReason?: any,
  ) => {
    try {
      const selectedConversation = chats[selectedChat].conversations[chatIndex];

      let payload: any = {
        // userid : `${chatIndex}${chats[selectedChat].sessionId}${chatIndex}`,
        // sessionid : chats[selectedChat].sessionId,
        questionId: chatIndex + 1,
        request: selectedConversation.query,
        feedBack: isLiked,
        dislikeReason: feedbackReason || "",
        fileList,
      };

      const res: { result: any; comparison?: any } = {
        result: selectedConversation.result,
      };
      const comparison = selectedConversation?.comparison;
      if (comparison && comparison.length > 0) {
        res.comparison = comparison;
      }
      payload.response = res;

      if (tool === "competitor") {
        // payload.sqlQuery = selectedConversation.sqlQuery;
        payload.displayType = selectedConversation.displyType;
      } else if (tool === "catalog" || tool === "document") {
        payload.themeName = selectedConversation?.raw?.themeName;
      }

      if (tool === "kyp") {
        payload = {
          messageId: selectedConversation?.id,
          feedback: isLiked,
          source: "fusion",
          module: "kyp",
        };
      }

      // const formattedPayload = { data: encrypt(JSON.stringify(payload)) };
      const formattedPayload = { data: payload, encryption: true };
      await getClient(tool).post("/feedback", formattedPayload);

      setChats((chats) => {
        selectedConversation.isLiked = isLiked;
        return [...chats];
      });
    } catch (err) {
      console.log(err);
    }
  };

  const fetchAnswer = async (conversationIndex: number, vectorName: any) => {
    try {
      const isValidPrompt = sanitizeUserInputQuery(
        chats[selectedChat].conversations[conversationIndex].query,
      );
      if (!isValidPrompt) throw new Error("Invalid Prompt");
      let payload: any = {};
      if (tool === "kyp") {
        const selectedPolicy = (
          activePolicies ? activePolicies : [{ policies: "" }]
        ).find((p: any) => p.isSelected)?.policies;
        payload = {
          userQuery: chats[selectedChat].conversations[conversationIndex].query,
          module: "kyp",
          source: "fusion",
          policyNumber: selectedPolicy,
          conversationId: chats[selectedChat].conversations[0]?.conversation_id,
        };
      } else {
        payload = {
          query: chats[selectedChat].conversations[conversationIndex].query,
          questionId: conversationIndex + 1,
          translationLanguage:
            chats[selectedChat].conversations[conversationIndex].locale,
          userConversation: chats[selectedChat].conversations
            .filter((conversation) => conversation.query && conversation.result)
            .slice(-10)
            .reduce((acc: any[], conversation) => {
              if (conversation.query && conversation.result) {
                acc.push({
                  author: "user",
                  data: conversation.query,
                  // html: conversation.query,
                });
                acc.push({ author: "bot", data: conversation.result });
              }
              return acc;
            }, []),
          // lastSql: previousConversation?.sqlQuery || "",
          datasetName: datasetName,
          perspective: perspective,
        };
      }
      if (["document", "qc", "legal"].includes(tool)) {
        payload.vector_name = vectorName;
        payload.files_list = fileList
          ?.filter((fileObj: Record<string, any>) => fileObj?.isSelected)
          ?.map((fileObj: Record<string, any>) =>
            fileObj?.name?.split("/")?.pop(),
          );
      }

      if (tool === "help") {
        payload.appId = applications?.filter(
          (obj) => obj?.isSelected,
        )?.[0]?.appId;
      }
      if (["document", "qc", "legal"].includes(tool)) {
        payload.inputType =
          chats[selectedChat].conversations[conversationIndex].inputType;
      }
      // const formattedPayload = { data: encrypt(JSON.stringify(payload)) };
      const formattedPayload = { data: payload, encryption: true };
      const response = await getClient(tool).post(
        "/analysis",
        formattedPayload,
        // { headers: { sessionId: chats[selectedChat].sessionId } }
      );

      // const data =
      //   tool === "kyp"
      //     ? JSON.parse(decrypt(response.data.data)).data
      //     : JSON.parse(decrypt(response.data.data));
      const data =
        tool === "kyp"
          ? response.data.data.data
          : response.data.data;
      setChats((chats) => {
        const conversation =
          chats?.[selectedChat]?.conversations?.[conversationIndex];
        if (conversation) {
          const updatedConversation = {
            ...conversation,
            isLoading: false,
            sqlQuery: data?.sql_query || "",
            result: data.result,
            isPlottable: data?.is_plottable || false,
            message: data?.message || "",
            displyType: data?.displyType || "",
            questionSuggestion: data.questionSuggestion,
            raw: data,
            url: data?.insuranceUrl || "",
            comparison: data?.comparison || null,
            conversation_id: data.conversation_id || null,
            id: data?.userMessage?.id || null,
          };
          const updatedChats = [...chats];
          updatedChats[selectedChat].conversations[conversationIndex] =
            updatedConversation;
          return updatedChats;
        }

        return chats;
      });
    } catch (err) {
      setChats((chats) => {
        const conversation =
          chats?.[selectedChat]?.conversations?.[conversationIndex];
        if (conversation) {
          const updatedConversation = {
            ...conversation,
            isLoading: false,
            isPlottable: false,
          };
          const updatedChats = [...chats];
          updatedChats[selectedChat].conversations[conversationIndex] =
            updatedConversation;
          return updatedChats;
        }
        return chats;
      });
    }
  };

  const fetchChatHistory = useCallback(() => {
    const payload = JSON.stringify({
      serviceName:
        tool === "document" ? docChatType : tool === "help" ? "Help Bot" : "",
    });

    // const encryptedPayload = { data: encrypt(payload) };
    const encryptedPayload = { data: payload, encryption: true };
    try {
      if (tool === "kyp") {
        const selectedPolicy = (
          activePolicies ? activePolicies : [{ policies: "" }]
        ).find((p: any) => p.isSelected)?.policies;
        // const kypPayload = encrypt(
        //   JSON.stringify({
        //     source: "fusion",
        //     module: "kyp",
        //     policyNumber: selectedPolicy,
        //   }),
        // );
        const kypPayload = {
          source: "fusion",
          module: "kyp",
          policyNumber: selectedPolicy,
        };
        getClient(tool)
          .post("/chat-history", { data: kypPayload, encryption: true })
          .then(({ data: { data } }) => {
            // let chatHistory = JSON.parse(decrypt(data))?.data || [];
            let chatHistory = data?.data || [];
            setLast5Queries([...chatHistory]?.slice(0, 5));
          })
          .catch((err) => {
            setLast5Queries([]);
          });
      } else if (tool === "wc-claims") {
        // fetch chat history for wc-claims
      } else {
        getClient(tool)
          .post("/get_chat_history", encryptedPayload)
          .then(({ data: { data } }) => {
            // let chatHistory =
            //   JSON.parse(decrypt(data))?.data?.chat_history || [];
            let chatHistory =
              data?.data?.chat_history || [];
            console.log(chatHistory);
            setLast5Queries([...chatHistory]);
          })
          .catch((err) => {
            setLast5Queries([]);
          });
      }
    } catch (error) {
      console.log(error);
    }
  }, [tool, docChatType, activePolicies]);

  const promptQueryFromHistory = async (queryObj: any, index: number) => {
    try {
      navigate("/company");

      const hasResult = last5Queries?.[index || 0]?.conversations?.[0]?.result;
      if (hasResult) {
        setSelectedQuery(index);
        return;
      }
      setLast5Queries((queries: any) => {
        queries[index].conversations[0] = {
          query: queryObj.query,
          locale: "en-US",
          isLoading: true,
        };
        return [...queries];
      });

      setTimeout(async () => {
        setSelectedQuery(index);
        let payload: any = {
          query: queryObj.query,
          questionId: 1,
          translationLanguage: "en-US",
          userConversation: [],
          lastSql: "",
          datasetName: datasetName,
          perspective: perspective,
        };

        // const formattedPayload = {
        //   data: encrypt(JSON.stringify(payload)),
        // };
        const formattedPayload = { data: payload, encryption: true };

        const response = await getClient(tool).post(
          "/analysis",
          formattedPayload,
          // { headers: { sessionId: chats[selectedChat].sessionId } }
        );

        // const data = JSON.parse(decrypt(response.data));
        const data = response.data;

        setLast5Queries((queries: any) => {
          const conversation = queries?.[index || 0]?.conversations?.[0];

          if (conversation) {
            const updatedConversation = {
              ...conversation,
              isLoading: false,
              sqlQuery: data.sql_query,
              result: data.result,
              isPlottable: data.is_plottable,
              message: data.message,
              displyType: data.displyType,
              questionSuggestion: data.questionSuggestion,
              raw: data,
              url: data.insuranceUrl,
              comparison: data.comparison,
            };

            const updatedChats = [...chats];
            updatedChats[selectedChat].conversations[index] =
              updatedConversation;
            return updatedChats;
          }
          return queries;
        });
      }, 100);
    } catch (err) {
      const conversation = last5Queries?.[index || 0]?.conversations?.[0];
      if (conversation) {
        conversation.isLoading = false;
        conversation.isPlottable = false;
      }
    }
  };

  const updateSuggestions = useCallback((values: string[]) => {
    setSuggestions(values);
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      let payload: any = { encryption: true };
      setSuggestions([]);
      if (tool === "competitor") {
        payload = { datasetName, perspective };
      } else if (tool === "catalog") {
        payload = { query: "", userConversation: [] };
      }
      if (!["document", "legal", "qc", "kyp", "help"].includes(tool)) {
        const response = await getClient(tool).post("/suggestion", payload);
        // const data = JSON.parse(decrypt(response.data.data));
        const data = response.data.data;
        setSuggestions(data || []);
      }
    } catch (err) {
      setSuggestions([]);
    }
  }, [tool]);

  useEffect(() => {
    scrollDown();
  }, [selectedChat, isChatLoading]);

  useEffect(() => {
    if (!source) {
      fetchChatHistory();
    }
    fetchSuggestions();
    setChats([getNewChat()]);
    setSelectedChat(0);
  }, [fetchChatHistory, fetchSuggestions, pathname, source]);

  // logic to refresh states when document type changes
  const refreshData = async () => {
    return new Promise((resolve) => {
      setSelectedChat(0);
      setSelectedQuery(null);
      setLast5Queries([]);
      setSuggestions([]);
      setSelectedSuggestion("");
      setFileList([]);
      setApplications(selfHelpApplications);
      setChats([]);
      resolve("ok");
    });
  };

  useEffect(() => {
    if (pathname === "/document") {
      refreshData().then(() => {
        if (!source) {
          fetchChatHistory();
        }
        fetchSuggestions();
        setChats([getNewChat()]);
      });
    }
  }, [docChatType]);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("selectedPolicies") || "null");
    setActivePolicies(data);
  }, []);

  return (
    <ChatbotContext.Provider
      value={{
        selectedQuery,
        setSelectedQuery,
        last5Queries,
        promptQueryFromHistory,
        selectedChat,
        setSelectedChat,
        passSuggestion,
        chats,
        selectedSuggestion,
        newChat,
        deleteChat,
        addConversation,
        addConversationFromHistory,
        conversationsContainerRef,
        suggestions,
        updateSuggestions,
        like,
        dislike,
        isChatLoading,
        fileList,
        setFileList,
        applications,
        setApplications,
        activePolicies,
        setActivePolicies,
        wcClaimDetails,
        setWCClaimDetails,
        isDocsProcessing,
        setIsDocsProcessing,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
}

export default function useChatbot(): IChatbotState {
  let context = useContext(ChatbotContext);
  return context!;
}
